import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, ClipboardList, Loader2 } from 'lucide-react';
import type { UserRole } from '../../types/cms';

interface AuditLogEntry {
  id: string;
  timestamp: string;
  username: string;
  role: UserRole;
  action: string;
  resource?: string;
  resourceId?: string;
  details?: Record<string, any>;
}

interface AuditLogProps {
  apiBase: string;
  token: string;
  showMessage: (msg: string) => void;
}

const actionLabels: Record<string, string> = {
  login: '登入',
  logout: '登出',
  cms_update: '更新網站資料',
  cms_reset: '重設網站資料',
  media_upload: '上傳媒體',
  media_update: '更新媒體資料',
  media_delete: '刪除媒體',
  media_purge_orphans: '清理孤兒媒體',
  media_migrate_r2: 'R2 遷移',
  inquiry_update: '更新查詢狀態',
  user_create: '建立帳號',
  user_update: '更新帳號',
  user_delete: '刪除帳號',
  user_password_change: '修改密碼',
  property_create: '新增物業',
  property_update: '更新物業',
  property_delete: '刪除物業',
  property_duplicate: '複製物業',
  fb_publish: '發佈到 Facebook',
  fb_publish_failed: 'FB 發佈失敗',
  video_generate: '生成影片',
  video_generate_failed: '影片生成失敗',
  ice_generate: 'ICE 生成影片',
  ice_generate_failed: 'ICE 生成失敗',
  shortlink_create: '建立短連結',
  handover_upload: '業主放盤上傳',
};

export const AuditLog: React.FC<AuditLogProps> = ({ apiBase, token, showMessage }) => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [search, setSearch] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/admin/audit-log`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setLogs(data.logs);
      else showMessage(`載入失敗：${data.error}`);
    } catch (err) {
      showMessage(`載入失敗：${String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [apiBase, token]);

  const users = useMemo(() => [...new Set(logs.map((l) => l.username))], [logs]);
  const actions = useMemo(() => [...new Set(logs.map((l) => l.action))], [logs]);
  const filtered = useMemo(
    () =>
      logs.filter(
        (l) =>
          (filterAction === 'all' || l.action === filterAction) &&
          (filterUser === 'all' || l.username === filterUser) &&
          (search === '' ||
            JSON.stringify(l).toLowerCase().includes(search.toLowerCase()) ||
            (actionLabels[l.action] || '').includes(search))
      ),
    [logs, filterAction, filterUser, search]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <ClipboardList className="h-5 w-5 text-blue-800" />
          操作日誌
        </h3>
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="flex items-center gap-1.5 rounded bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          重新整理
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-4 py-3 text-left">時間</th>
              <th className="px-4 py-3 text-left">用戶</th>
              <th className="px-4 py-3 text-left">角色</th>
              <th className="px-4 py-3 text-left">動作</th>
              <th className="px-4 py-3 text-left">資源</th>
              <th className="px-4 py-3 text-left">詳情</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  暫無記錄
                </td>
              </tr>
            )}
            {logs.map((log) => (
              <tr key={log.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                  {new Date(log.timestamp).toLocaleString('zh-HK')}
                </td>
                <td className="px-4 py-3 font-semibold">{log.username}</td>
                <td className="px-4 py-3">
                  <span className={`rounded px-2 py-0.5 text-xs font-bold ${log.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                    {log.role}
                  </span>
                </td>
                <td className="px-4 py-3">{actionLabels[log.action] || log.action}</td>
                <td className="px-4 py-3 text-slate-600">
                  {log.resource ? `${log.resource}${log.resourceId ? ` (${log.resourceId})` : ''}` : '-'}
                </td>
                <td className="px-4 py-3 max-w-xs truncate text-slate-500">
                  {log.details ? JSON.stringify(log.details) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditLog;
